/**
 * RequireRoles — route-level RBAC guard (audit item M4).
 *
 * The sidebar already hides links a user can't use (AppShell `leafVisibleToUser`),
 * but the ROUTES weren't gated — a user could reach a restricted page by typing
 * the URL. This wraps a route's element with the SAME role logic as the nav
 * (session JWT roles + super-user bypass), so visibility and reachability agree.
 *
 * Client gating is UX, not security — the API is the real enforcement boundary.
 */
import type { ComponentType, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../auth/SessionProvider';
import { isSuperUser } from '../AppShell/AppShell';
import styles from './RequireRoles.module.css';

export function RequireRoles({ roles, children }: { roles: string[]; children: ReactNode }) {
  const { t } = useTranslation();
  const { session } = useSession();
  const userRoles = session?.roles ?? [];

  const allowed =
    isSuperUser(userRoles) ||
    roles.length === 0 ||
    roles.some((r) => userRoles.includes(r));

  if (allowed) return <>{children}</>;

  return (
    <main className={styles.guard} role="alert">
      <h1 className={styles.title}>{t('rbac.restricted.title', { defaultValue: 'Restricted' })}</h1>
      <p className={styles.body}>
        {t('rbac.restricted.body', {
          defaultValue: 'You don’t have a role that can view this page. Talk to your QWise admin if you need access.',
        })}
      </p>
      <p className={styles.roles}>
        {t('rbac.restricted.needs', { defaultValue: 'Requires one of' })}: {roles.join(', ')}
      </p>
    </main>
  );
}

/** HOC: wrap a route component so the route only renders for the given roles.
 * Returns a plain function component (TanStack's RouteComponent type rejects
 * the wider ComponentType, which also admits class components). */
export function gated<P extends object>(Component: ComponentType<P>, roles: string[]) {
  const Gated = (props: P) => (
    <RequireRoles roles={roles}>
      <Component {...props} />
    </RequireRoles>
  );
  Gated.displayName = `gated(${Component.displayName || Component.name || 'Component'})`;
  return Gated;
}
